import { useState, useEffect, useCallback } from 'react';
import { eventBookingCategoryService } from '@/features/booking/services/eventBookingCategoryService';
import type {
  ProcessedBookingCategory,
  EventScheduleResponse,
  EventBookingCategoryResponse
} from '@/features/booking/types/eventBookingCategoryTypes';

interface UseEventBookingCategoriesResult {
  bookingCategories: ProcessedBookingCategory[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook for fetching and processing event booking categories
 * @param eventScheduleId - The ID of the event schedule
 * @param bookingType - Type of booking ('tickets' or 'tables')
 * @returns Object containing booking categories, loading state, error, and refetch function
 */
export const useEventBookingCategories = (
  eventScheduleId: string,
  bookingType: 'tickets' | 'tables'
): UseEventBookingCategoriesResult => {
  const [bookingCategories, setBookingCategories] = useState<ProcessedBookingCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const processBookingCategories = useCallback((
    eventSchedule: EventScheduleResponse
  ): ProcessedBookingCategory[] => {
    const categories = eventSchedule.event_booking_categories || [];

    return categories
      .filter((item: EventBookingCategoryResponse) => {
        // Ensure booking_category exists and is not null
        if (!item.booking_category) {
          console.warn('Event booking category missing booking_category:', item);
          return false;
        }

        // Filter based on booking type
        if (bookingType === 'tickets') {
          return !item.booking_category.is_table_booking;
        } else {
          return item.booking_category.is_table_booking;
        }
      })
      .map((item: EventBookingCategoryResponse) => ({
        id: item.id,
        name: item.booking_category.name,
        description: item.booking_category.description,
        price: item.price,
        currency: (() => {
          if (!item.currency) {
            console.warn('useEventBookingCategories: currency missing from API response for category:', item.id);
          }
          return item.currency || '';
        })(),
        cancellation_fees: item.cancellation_fees,
        is_table_booking: item.booking_category.is_table_booking,
        item_type: item.booking_category.item_type || 'UNKNOWN',
        card_image_id: item.booking_category.card_image_id,
        card_video_id: item.booking_category.card_video_id,
        perks: item.booking_category.perks || null,
        table_categories: item.booking_category.table_categories || null,
        capacity: item.capacity,
        remaining_capacity: item.remaining_capacity,
        business: item.booking_category.business as ProcessedBookingCategory['business'],
        event: {
          ...eventSchedule.event,
          dress_code: eventSchedule.event.dress_code || '',
          sponsors: eventSchedule.event.sponsors || [],
          tier: eventSchedule.event.tier || 0,
          business: eventSchedule.event.business as ProcessedBookingCategory['event']['business'],
        },
        event_schedule: {
          id: eventSchedule.id,
          start_time: eventSchedule.start_time,
          duration: eventSchedule.duration,
          early_entry_duration: eventSchedule.early_entry_duration,
          cancelled: eventSchedule.cancelled,
          event: eventSchedule.event as unknown as ProcessedBookingCategory['event_schedule']['event'],
          event_booking_categories: [],
          artists: eventSchedule.artists,
          add_on_schedules: eventSchedule.add_on_schedules,
        } as unknown as ProcessedBookingCategory['event_schedule'],
      })) as ProcessedBookingCategory[];
  }, [bookingType]);

  const fetchBookingCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await eventBookingCategoryService.getEventSchedule(eventScheduleId);

      const processedCategories = processBookingCategories(response.data["event-schedule"]);
      setBookingCategories(processedCategories);
    } catch (err) {
      console.error('Error fetching booking categories:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch booking categories');
      setBookingCategories([]);
    } finally {
      setLoading(false);
    }
  }, [eventScheduleId, processBookingCategories]);

  useEffect(() => {
    if (eventScheduleId) {
      fetchBookingCategories();
    }
  }, [eventScheduleId, fetchBookingCategories]);

  return {
    bookingCategories,
    loading,
    error,
    refetch: fetchBookingCategories,
  };
};
